import React, { useState } from 'react';
import { connect } from 'react-redux';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { useConfiguration } from '../../context/configurationContext';
import { LayoutComposer, Page, PageHero } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import { CANOPY_DATABASE } from './canopyDatabase';
import css from './WingloadingCalculatorPage.module.css';

const IMG = '/static/images/wl-calculator';

/* ── Helpers ── */

function computeWingLoading(exitWeight, unit, canopySize) {
  const w = parseFloat(exitWeight);
  const s = parseFloat(canopySize);
  if (!Number.isFinite(w) || !Number.isFinite(s) || w <= 0 || s <= 0) return null;
  const weightLb = unit === 'kg' ? w * 2.20462 : w;
  return weightLb / s;
}

function filterCanopies(wl, database) {
  if (wl == null) return [];
  return database.filter(c => {
    const aboveMin = Number.isFinite(c.wlMin) ? wl >= c.wlMin : true;
    const belowMax = Number.isFinite(c.wlMax) ? wl <= c.wlMax : true;
    return aboveMin && belowMax;
  });
}

const BRAND_ORDER = [
  'JYRO',
  'Performance Design',
  'Aerodyne',
  'Fluid Wings',
  'Icarus World',
  'Skylark',
  'Paratec',
  'Atair',
  'Squirrel',
];

function groupByBrand(matches) {
  const groups = {};
  matches.forEach(c => {
    const b = c.brand || 'Unknown';
    (groups[b] ||= []).push(c);
  });
  return Object.keys(groups)
    .sort((a, b) => {
      const ai = BRAND_ORDER.indexOf(a);
      const bi = BRAND_ORDER.indexOf(b);
      if (ai === -1 && bi === -1) return a.localeCompare(b);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    })
    .map(brand => ({
      brand,
      canopies: groups[brand].sort((a, b) => a.name.localeCompare(b.name)),
    }));
}

const LEVEL_MAP = {
  beginner: 'chipBeginner',
  intermediate: 'chipIntermediate',
  transition: 'chipIntermediate',
  advanced: 'chipAdvanced',
  expert: 'chipExpert',
  competition: 'chipExpert',
  wingsuit: 'chip',
  accuracy: 'chip',
};

function chipClass(level) {
  if (!level) return css.chip;
  const key = String(level).toLowerCase().trim();
  const mapped = LEVEL_MAP[key];
  return mapped ? css[mapped] : css.chip;
}

function prettyLevel(level) {
  if (!level) return '';
  const s = String(level).trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/* ── Schema ── */

function buildPageSchema(rootURL) {
  return {
    '@type': 'WebApplication',
    name: 'Avento Wing Loading Calculator',
    url: `${rootURL}/wingloading-calculator`,
    description:
      'Free online wing loading calculator for skydivers. Calculate your wing loading from exit weight and canopy size, and get matching canopy model recommendations from trusted manufacturer data.',
    applicationCategory: 'UtilityApplication',
    applicationSubCategory: 'Skydiving Calculator',
    operatingSystem: 'Web',
    browserRequirements: 'Requires JavaScript',
    inLanguage: 'en',
    featureList: [
      'Wing loading calculation from exit weight (kg or lb) and canopy size',
      'Matching canopy recommendations grouped by brand',
      'Skill-level labels (beginner, intermediate, advanced, expert) on each match',
      'Internal database covering JYRO, Performance Designs, Aerodyne, Fluid Wings, Icarus World, Skylark, Paratec, Atair, Squirrel',
    ],
    audience: {
      '@type': 'Audience',
      audienceType: 'Skydivers selecting or downsizing a parachute canopy',
    },
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'EUR',
    },
    author: {
      '@type': 'Organization',
      '@id': `${rootURL}#organization`,
      name: 'Avento Gear GmbH',
      url: rootURL,
    },
  };
}

const layoutAreas = `
  topbar
  main
  footer
`;

const WingloadingCalculatorPageComponent = props => {
  const { scrollingDisabled } = props;
  const config = useConfiguration();

  const schemaTitle = 'Smart Wing Loading Calculator | Avento Skydiving Resources';
  const schemaDescription =
    'Calculate your wing loading and get matching canopy recommendations. Free skydiving calculator to find new canopy models.';

  const rootURL = config.marketplaceRootURL || '';
  const pageSchema = buildPageSchema(rootURL);
  const breadcrumbSchema = {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${rootURL}/` },
      { '@type': 'ListItem', position: 2, name: 'Wing Loading Calculator', item: `${rootURL}/wingloading-calculator` },
    ],
  };

  const [exitWeight, setExitWeight] = useState('');
  const [unit, setUnit] = useState('lb');
  const [canopySize, setCanopySize] = useState('');
  const [wingLoading, setWingLoading] = useState(null);
  const [matches, setMatches] = useState([]);
  const [hasCalculated, setHasCalculated] = useState(false);

  const handleCalculate = () => {
    const wl = computeWingLoading(exitWeight, unit, canopySize);
    setWingLoading(wl);
    setMatches(wl != null ? filterCanopies(wl, CANOPY_DATABASE) : []);
    setHasCalculated(true);
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCalculate();
    }
  };

  const grouped = groupByBrand(matches);

  return (
    <Page
      title={schemaTitle}
      description={schemaDescription}
      schema={[pageSchema, breadcrumbSchema]}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutComposer areas={layoutAreas} className={css.layout}>
        {layoutProps => {
          const { Topbar, Main, Footer } = layoutProps;
          return (
            <>
              <Topbar as="header" className={css.topbar}>
                <TopbarContainer currentPage="WingloadingCalculatorPage" />
              </Topbar>
              <Main as="main" className={css.main}>

                {/* Hero */}
                <PageHero
                  title="Wingloading Calculator"
                  subtitle="Calculate your wing loading and find canopies that match your range"
                  desktopImage={`${IMG}/hero-desktop.webp`}
                  desktopWidth={3024}
                  desktopHeight={700}
                  mobileImage={`${IMG}/hero-mobile.webp`}
                  mobileWidth={780}
                  mobileHeight={571}
                />

                {/* Info */}
                <section className={css.section}>
                  <div className={css.container}>
                    <h2 className={css.sectionHeading}>About This Tool</h2>
                    <p className={css.paragraph}>
                      Enter your exit weight and canopy size to calculate your wing loading.
                      The calculator will show canopies from our database that match the
                      recommended range.
                    </p>
                    <div className={css.infoNote}>
                      <strong>Warning:</strong> Recommendations are informational only and
                      intended to allow you to explore what canopies exist that fit your
                      wingloading.
                    </div>
                  </div>
                </section>

                {/* Calculator */}
                <section className={css.calculatorSection}>
                  <div className={css.container}>
                    <div className={css.card}>
                      <div className={css.wlGrid}>
                        <div>
                          <h3 className={css.cardHeading}>Calculate your wingloading</h3>
                          <p className={css.cardDescription}>
                            Enter your exit weight and canopy size. We'll calculate your
                            wingloading and show canopies that match the recommended range from
                            our database.
                          </p>

                          <div className={css.formGrid}>
                            <div className={css.field}>
                              <label className={css.label} htmlFor="wl-exit-weight">
                                Exit weight
                              </label>
                              <div className={css.weightWrap}>
                                <input
                                  id="wl-exit-weight"
                                  type="number"
                                  className={css.weightInput}
                                  placeholder={unit === 'kg' ? 'e.g., 80' : 'e.g., 180'}
                                  value={exitWeight}
                                  onChange={e => setExitWeight(e.target.value)}
                                  onKeyDown={handleKeyDown}
                                  min="0"
                                  inputMode="decimal"
                                />
                                <select
                                  className={css.unitSelect}
                                  value={unit}
                                  onChange={e => setUnit(e.target.value)}
                                  aria-label="Weight unit"
                                >
                                  <option value="lb">lb</option>
                                  <option value="kg">kg</option>
                                </select>
                              </div>
                            </div>

                            <div className={css.field}>
                              <label className={css.label} htmlFor="wl-canopy-size">
                                Canopy size (sqft)
                              </label>
                              <input
                                id="wl-canopy-size"
                                type="number"
                                className={css.input}
                                placeholder="e.g., 170"
                                value={canopySize}
                                onChange={e => setCanopySize(e.target.value)}
                                onKeyDown={handleKeyDown}
                                min="0"
                                inputMode="decimal"
                              />
                            </div>
                          </div>

                          <div className={css.actions}>
                            <button
                              type="button"
                              className={css.btnPrimary}
                              onClick={handleCalculate}
                            >
                              Calculate
                            </button>
                          </div>
                        </div>

                        <aside className={css.wlSide}>
                          <p className={css.wlSideLabel}>Your wingloading</p>
                          <p className={css.wlBig}>
                            {wingLoading != null ? wingLoading.toFixed(2) : '\u2013'}
                          </p>
                          <p className={css.wlNote}>
                            {!hasCalculated
                              ? 'Enter values to calculate.'
                              : wingLoading != null
                              ? 'Matching canopies below are filtered by this wingloading.'
                              : 'Check your inputs.'}
                          </p>
                        </aside>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Results */}
                {hasCalculated && (
                  <section className={css.resultsSection}>
                    <div className={css.container}>
                      <h2 className={css.resultsHeading}>Recommended Canopies</h2>

                      {grouped.length > 0 ? (
                        grouped.map(group => (
                          <div key={group.brand} className={css.brandGroup}>
                            <div className={css.brandHeader}>
                              <div className={css.brandLeft}>
                                <span className={css.brandPill} />
                                <span className={css.brandName}>{group.brand}</span>
                              </div>
                              <span className={css.matchCount}>
                                {group.canopies.length}{' '}
                                {group.canopies.length === 1 ? 'canopy' : 'canopies'}
                              </span>
                            </div>
                            {group.canopies.map(c => (
                              <div key={`${c.brand}-${c.name}`} className={css.canopyItem}>
                                <a
                                  href={c.website}
                                  className={css.canopyName}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {c.name}
                                </a>
                                <div className={css.canopyMeta}>
                                  <span className={css.canopyRange}>
                                    {Number.isFinite(c.wlMin) ? c.wlMin.toFixed(2) : '?'}
                                    {' - '}
                                    {Number.isFinite(c.wlMax) ? c.wlMax.toFixed(2) : '?'}
                                  </span>
                                  <span className={chipClass(c.level)}>
                                    {prettyLevel(c.level)}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        ))
                      ) : (
                        <div className={css.emptyState}>
                          <p className={css.emptyTitle}>No matches found</p>
                          <p className={css.emptyText}>
                            Try another size or check your inputs.
                          </p>
                        </div>
                      )}
                    </div>
                  </section>
                )}

              </Main>
              <Footer>
                <FooterContainer />
              </Footer>
            </>
          );
        }}
      </LayoutComposer>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const WingloadingCalculatorPage = connect(mapStateToProps)(
  WingloadingCalculatorPageComponent
);

export default WingloadingCalculatorPage;
